import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Check, X, Clock, Users, BarChart2, Crown, Calendar, Tv, Save, RefreshCw, Trash2, Cloud, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkedArticleSelect = ({ game, articles, password, onSaved }) => {
  const save = async (articleId) => {
    try {
      await axios.post(`${API}/pickem/admin/games/${game.id}/linked-article`, { article_id: articleId || null }, { params: { password } });
      toast.success(articleId ? 'Article linked to game' : 'Article unlinked');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link article');
    }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
      <span className="text-sm font-medium text-slate-600 whitespace-nowrap">Linked article:</span>
      <select
        value={game.linked_article_id || ''}
        onChange={(e) => save(e.target.value)}
        className="flex-1 border rounded px-2 py-1 text-sm"
        data-testid={`linked-article-select-${game.id}`}
      >
        <option value="">— None —</option>
        {articles.map(a => <option key={a.article_id} value={a.article_id}>{a.title}</option>)}
      </select>
    </div>
  );
};

const AdminPickemPage = () => {
  const [games, setGames] = useState([]);
  const [articles, setArticles] = useState([]);
  const [allWeeks, setAllWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [shelovestheePicks, setShelovestheePicks] = useState({});
  const [localPicks, setLocalPicks] = useState({});
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gameResults, setGameResults] = useState({});
  const [resetting, setResetting] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [rescoringGame, setRescoringGame] = useState(null);
  const [cloudStatus, setCloudStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Check cloud sync status
  const checkCloudStatus = async () => {
    try {
      const res = await axios.get(`${API}/pickem/cloud/status`);
      setCloudStatus(res.data);
    } catch (error) {
      console.error('Failed to check cloud status:', error);
    }
  };
  
  // Publish to cloud
  const publishToCloud = async () => {
    if (!window.confirm('📤 Publish Pick\'em data to cloud?\n\nThis will save all teams, weeks, games, and picks to cloud storage for syncing to Production.')) {
      return;
    }
    
    try {
      setPublishing(true);
      const res = await axios.post(`${API}/pickem/cloud/publish`, null, {
        params: { password }
      });
      toast.success(`✅ ${res.data.message}\nTeams: ${res.data.published.teams}, Weeks: ${res.data.published.weeks}, Games: ${res.data.published.games}`);
      checkCloudStatus();
    } catch (error) {
      toast.error('Failed to publish to cloud');
      console.error(error);
    } finally {
      setPublishing(false);
    }
  };
  
  // Sync from cloud
  const syncFromCloud = async () => {
    if (!window.confirm('📥 Sync Pick\'em data from cloud?\n\nThis will import all teams, weeks, games, and picks from cloud storage.\n\nUse this on Production to get Week 1 games!')) {
      return;
    }
    
    try {
      setSyncing(true);
      const res = await axios.post(`${API}/pickem/cloud/sync`, null, {
        params: { password }
      });
      toast.success(`✅ ${res.data.message}\nTeams: ${res.data.synced.teams}, Weeks: ${res.data.synced.weeks}, Games: ${res.data.synced.games}`);
      // Re-fetch weeks to get updated list after sync
      const weeksRes = await axios.get(`${API}/pickem/weeks`);
      setAllWeeks(weeksRes.data);
      loadData(password, selectedWeekId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from cloud');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };
  
  // Reset picks only (keeps games/teams)
  const resetAllData = async () => {
    if (!window.confirm('⚠️ Reset all PICKS?\n\nThis will delete all user picks and shelovesthee picks.\nGames and teams will be preserved.\n\nContinue?')) {
      return;
    }
    
    try {
      setResetting(true);
      const res = await axios.post(`${API}/pickem/admin/reset-all-data`, null, {
        params: { password }
      });
      toast.success('All picks have been reset!');
      console.log('Reset result:', res.data);
      loadData(password, selectedWeekId);
    } catch (error) {
      toast.error('Failed to reset data');
      console.error(error);
    } finally {
      setResetting(false);
    }
  };
  
  // Re-seed all data (teams, games, season, week)
  const reseedData = async () => {
    if (!window.confirm('🔄 Re-seed ALL Pick\'em data?\n\nThis will:\n- Delete all existing data\n- Recreate teams, games, season, and week\n\nUse this if games/teams are missing!')) {
      return;
    }
    
    try {
      setReseeding(true);
      const res = await axios.post(`${API}/pickem/admin/reseed-data`, null, {
        params: { password }
      });
      toast.success('Data has been re-seeded!');
      console.log('Reseed result:', res.data);
      
      // Reload page to get fresh data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to re-seed data');
      console.error(error);
    } finally {
      setReseeding(false);
    }
  };
  
  // Rescore all games - recalculates is_correct for all picks
  const rescoreAllGames = async () => {
    if (!window.confirm('📊 Rescore ALL games?\n\nThis will recalculate correct/incorrect for ALL picks across all final games.\n\nUse this to fix any scoring issues.')) {
      return;
    }
    
    try {
      setRescoring(true);
      const res = await axios.post(`${API}/pickem/admin/rescore-all-games`, null, {
        params: { password }
      });
      toast.success(`Rescored ${res.data.games_processed || 0} games, ${res.data.picks_updated || 0} picks updated!`);
      console.log('Rescore result:', res.data);
      loadData(password, selectedWeekId);
    } catch (error) {
      toast.error('Failed to rescore games');
      console.error(error);
    } finally {
      setRescoring(false);
    }
  };
  
  // Rescore a single game
  const rescoreGame = async (gameId) => {
    try {
      setRescoringGame(gameId);
      const res = await axios.post(`${API}/pickem/admin/rescore-game/${gameId}`, null, {
        params: { password }
      });
      toast.success(`Game rescored! ${res.data.picks_updated || 0} picks updated.`);
      loadData(password, selectedWeekId);
    } catch (error) {
      toast.error('Failed to rescore game');
      console.error(error);
    } finally {
      setRescoringGame(null);
    }
  };
  
  // Check password and load data
  const authenticate = async (pwd) => {
    try {
      const res = await axios.get(`${API}/pickem/admin/analytics`, {
        params: { password: pwd }
      });
      setAnalytics(res.data);
      setIsAuthenticated(true);
      setPassword(pwd);
      
      // Load weeks first, then data
      const weeksRes = await axios.get(`${API}/pickem/weeks`);
      setAllWeeks(weeksRes.data);
      
      const currentRes = await axios.get(`${API}/pickem/weeks/current`);
      setCurrentWeek(currentRes.data);
      setSelectedWeekId(currentRes.data.id);
      
      loadData(pwd, currentRes.data.id);
      checkCloudStatus();
    } catch (error) {
      toast.error('Invalid password');
      setIsAuthenticated(false);
    }
  };
  
  const fetchGames = async () => {
    if (!currentWeek) return;
    const res = await axios.get(`${API}/pickem/games`, { params: { week_id: currentWeek.id } });
    setGames(res.data);
  };

  useEffect(() => {
    axios.get(`${API}/interactive-articles`, { params: { status: 'published', limit: 100 } })
      .then(r => setArticles(r.data.articles || []))
      .catch(() => {});
  }, []);

  const loadData = async (pwd, weekIdToLoad = null) => {
    try {
      setLoading(true);
      
      // Use specified week or selected week or fall back to current
      const targetWeekId = weekIdToLoad || selectedWeekId;
      
      let weekData;
      if (targetWeekId) {
        // Find week from allWeeks or fetch current
        weekData = allWeeks.find(w => w.id === targetWeekId);
        if (!weekData) {
          const weekRes = await axios.get(`${API}/pickem/weeks/current`);
          weekData = weekRes.data;
          setSelectedWeekId(weekData.id);
        }
      } else {
        const weekRes = await axios.get(`${API}/pickem/weeks/current`);
        weekData = weekRes.data;
        setSelectedWeekId(weekData.id);
      }
      setCurrentWeek(weekData);
      
      // Get games for selected week
      const gamesRes = await axios.get(`${API}/pickem/games`, {
        params: { week_id: weekData.id }
      });
      setGames(gamesRes.data);
      
      // Initialize game results
      const results = {};
      gamesRes.data.forEach(g => {
        results[g.id] = {
          winner_team_id: g.winner_team_id || '',
          away_score: g.away_score ?? '',
          home_score: g.home_score ?? '',
          status: g.status || 'scheduled'
        };
      });
      setGameResults(results);
      
      // Get SheLovesThee picks
      const sheRes = await axios.get(`${API}/pickem/shelovesthee-picks`, {
        params: { week_id: weekData.id }
      });
      setShelovestheePicks(sheRes.data);
      setLocalPicks(sheRes.data);
      
      // Get analytics
      const analyticsRes = await axios.get(`${API}/pickem/admin/analytics`, {
        params: { password: pwd }
      });
      setAnalytics(analyticsRes.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Save SheLovesThee pick for a game
  const saveSheLovesTheePick = async (gameId, teamId) => {
    try {
      await axios.post(`${API}/pickem/admin/shelovesthee-pick`, null, {
        params: { game_id: gameId, selected_team_id: teamId, password }
      });
      setShelovestheePicks(prev => ({ ...prev, [gameId]: teamId }));
      toast.success('shelovesthee pick saved!');
    } catch (error) {
      toast.error('Failed to save pick');
    }
  };
  
  // Save game result
  const saveGameResult = async (gameId) => {
    const result = gameResults[gameId];
    if (!result || !result.winner_team_id) {
      toast.error('Please select a winner');
      return;
    }
    
    try {
      await axios.post(`${API}/pickem/admin/games/${gameId}/result`, {
        status: 'final',
        winner_team_id: result.winner_team_id,
        away_score: result.away_score ? parseInt(result.away_score) : null,
        home_score: result.home_score ? parseInt(result.home_score) : null
      }, {
        params: { password }
      });
      toast.success('Game result saved and picks scored!');
      loadData(password, selectedWeekId);
    } catch (error) {
      toast.error('Failed to save result');
    }
  };
  
  const formatKickoffTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  
  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <Trophy className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-slate-800">SWAC Pick&apos;em Admin</h1>
            <p className="text-slate-500">Enter admin password to continue</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); authenticate(password); }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin Password"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="admin-password-input"
            />
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3"
              data-testid="admin-login-btn"
            >
              Access Admin Panel
            </Button>
          </form>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-pickem-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00204E] to-[#003366] py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Trophy className="h-6 w-6 text-yellow-400" />
                SWAC Pick&apos;em Admin
              </h1>
              {/* Week Selector Dropdown */}
              {allWeeks.length > 0 && (
                <div className="mt-2 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-3 py-2 rounded-lg">
                  <Calendar className="h-4 w-4" />
                  <select
                    value={selectedWeekId || ''}
                    onChange={(e) => {
                      setSelectedWeekId(e.target.value);
                      loadData(password, e.target.value);
                    }}
                    className="bg-transparent text-white font-bold border-none focus:outline-none cursor-pointer appearance-none pr-6"
                    style={{ 
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'white\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")', 
                      backgroundRepeat: 'no-repeat', 
                      backgroundPosition: 'right center', 
                      backgroundSize: '20px' 
                    }}
                    data-testid="admin-week-selector"
                  >
                    {allWeeks.map(week => (
                      <option key={week.id} value={week.id} className="text-black">
                        Week {week.week_number} {week.is_open ? '(Active)' : '(Closed)'} - {week.games_count} games
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                onClick={() => loadData(password, selectedWeekId)}
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                data-testid="admin-refresh-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button 
                onClick={rescoreAllGames}
                disabled={rescoring}
                variant="outline"
                className="border-yellow-400 text-yellow-400 hover:bg-yellow-500/20"
                data-testid="rescore-all-games-btn"
              >
                <BarChart2 className="h-4 w-4 mr-2" /> {rescoring ? 'Rescoring...' : 'Rescore All'}
              </Button>
              <Button 
                onClick={reseedData}
                disabled={reseeding}
                variant="outline"
                className="border-green-400 text-green-400 hover:bg-green-500/20"
                data-testid="reseed-data-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> {reseeding ? 'Re-seeding...' : 'Re-Seed Games'}
              </Button>
              <Button 
                onClick={resetAllData}
                disabled={resetting}
                variant="outline"
                className="border-red-400 text-red-400 hover:bg-red-500/20"
                data-testid="reset-picks-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> {resetting ? 'Resetting...' : 'Clear Picks'}
              </Button>
            </div>
          </div>
          
          {/* Cloud Sync Section */}
          <div className="mt-4 bg-white/10 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Cloud className="h-5 w-5 text-blue-300" />
                <span className="font-medium">Cloud Sync</span>
                {cloudStatus?.hasCloudData && (
                  <span className="text-xs text-blue-200">
                    (Last: {new Date(cloudStatus.publishedAt).toLocaleString()} • {cloudStatus.games} games)
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={publishToCloud}
                  disabled={publishing}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="publish-to-cloud-btn"
                >
                  <Cloud className="h-4 w-4 mr-1" />
                  {publishing ? 'Publishing...' : 'Publish to Cloud'}
                </Button>
                <Button
                  onClick={syncFromCloud}
                  disabled={syncing || !cloudStatus?.hasCloudData}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="sync-from-cloud-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  {syncing ? 'Syncing...' : 'Sync from Cloud'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Analytics Cards */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="flex items-center gap-3">
              <Users className="h-10 w-10 text-blue-500" />
              <div>
                <div className="text-3xl font-bold text-slate-800">{analytics?.total_participants || 0}</div>
                <div className="text-sm text-slate-500">Total Participants</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="flex items-center gap-3">
              <BarChart2 className="h-10 w-10 text-green-500" />
              <div>
                <div className="text-3xl font-bold text-slate-800">{analytics?.total_picks || 0}</div>
                <div className="text-sm text-slate-500">Total Picks</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow">
            <div className="flex items-center gap-3">
              <Trophy className="h-10 w-10 text-yellow-500" />
              <div>
                <div className="text-xl font-bold text-slate-800">{analytics?.most_picked_team?.name || '-'}</div>
                <div className="text-sm text-slate-500">Most Picked Team ({analytics?.most_picked_team?.picks || 0} picks)</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Games Management */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-800">
            Week {allWeeks.find(w => w.id === selectedWeekId)?.week_number ?? currentWeek?.week_number} Games
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({games.length} games)
            </span>
          </h2>
          {games.length === 0 && (
            <span className="text-amber-600 text-sm font-medium">
              No games found for this week. Add games or sync from cloud.
            </span>
          )}
        </div>
        
        <div className="space-y-4">
          {games.map(game => (
            <div 
              key={game.id} 
              className={`bg-white rounded-xl shadow overflow-hidden ${game.is_game_of_week ? 'ring-2 ring-yellow-400' : ''}`}
              data-testid={`admin-game-card-${game.id}`}
            >
              {/* Game Header */}
              <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-600">{formatKickoffTime(game.kickoff_time)}</span>
                  <span className="text-slate-400">|</span>
                  <Tv className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">{game.network}</span>
                </div>
                <div className="flex items-center gap-2">
                  {game.is_game_of_week && (
                    <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-1 rounded">⭐ GOTW</span>
                  )}
                  {game.is_locked ? (
                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">🔒 LOCKED</span>
                  ) : (
                    <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded">OPEN</span>
                  )}
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    game.status === 'final' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {game.status.toUpperCase()}
                  </span>
                </div>
              </div>
              
              {/* Game Content */}
              <div className="p-4">
                {/* SECTION 1: SheLovesThee Pick - Click to save instantly */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-slate-500 flex items-center gap-2">
                      <Crown className="h-4 w-4 text-yellow-500" />
                      shelovesthee Pick
                    </h3>
                    <span className="text-xs text-slate-400 italic">Click a team to save your pick</span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Away Team */}
                    <button
                      onClick={() => saveSheLovesTheePick(game.id, game.away_team.id)}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        shelovestheePicks[game.id] === game.away_team.id
                          ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-300'
                          : 'border-slate-200 hover:border-yellow-400 hover:bg-yellow-50/50'
                      }`}
                    >
                      <img 
                        src={game.away_team?.logo_url} 
                        alt={game.away_team?.name}
                        className="h-14 w-14 object-contain mx-auto mb-2"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=' + game.away_team?.abbreviation; }}
                      />
                      <p className="font-bold text-slate-800 text-center">{game.away_team?.abbreviation}</p>
                      <p className="text-xs text-slate-500 text-center">{game.away_team?.nickname}</p>
                      {shelovestheePicks[game.id] === game.away_team.id && (
                        <div className="mt-2 bg-yellow-500 text-white text-xs font-bold py-1 px-2 rounded-full text-center">
                          👑 YOUR PICK
                        </div>
                      )}
                    </button>
                    
                    <div className="flex flex-col items-center">
                      <span className="text-2xl font-bold text-slate-300">@</span>
                    </div>
                    
                    {/* Home Team */}
                    <button
                      onClick={() => saveSheLovesTheePick(game.id, game.home_team.id)}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        shelovestheePicks[game.id] === game.home_team.id
                          ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-300'
                          : 'border-slate-200 hover:border-yellow-400 hover:bg-yellow-50/50'
                      }`}
                    >
                      <img 
                        src={game.home_team?.logo_url} 
                        alt={game.home_team?.name}
                        className="h-14 w-14 object-contain mx-auto mb-2"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=' + game.home_team?.abbreviation; }}
                      />
                      <p className="font-bold text-slate-800 text-center">{game.home_team?.abbreviation}</p>
                      <p className="text-xs text-slate-500 text-center">{game.home_team?.nickname}</p>
                      {shelovestheePicks[game.id] === game.home_team.id && (
                        <div className="mt-2 bg-yellow-500 text-white text-xs font-bold py-1 px-2 rounded-full text-center">
                          👑 YOUR PICK
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {/* Pick Status */}
                  {shelovestheePicks[game.id] ? (
                    <div className="mt-3 flex items-center justify-center gap-2 text-green-600 text-sm">
                      <Check className="h-4 w-4" />
                      <span>Pick saved! You can change it anytime before kickoff.</span>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center justify-center gap-2 text-amber-600 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>No pick yet - click a team above</span>
                    </div>
                  )}
                </div>
                
                {/* Divider */}
                <div className="border-t border-slate-200 my-4"></div>
                
                {/* SECTION 2: Enter Final Score (After Game) */}
                <details className="group">
                  <summary className="cursor-pointer flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                    <h3 className="text-sm font-bold text-slate-500 flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-blue-500" />
                      Enter Final Score
                      {game.status === 'final' && <span className="text-green-500 text-xs ml-2">✓ Completed</span>}
                    </h3>
                    <span className="text-xs text-slate-400 group-open:hidden">Click to expand (after game ends)</span>
                  </summary>
                  
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-3">
                    {/* Score Input */}
                    <div className="flex items-center justify-center gap-3">
                      <div className="text-center">
                        <span className="text-xs text-slate-500 block mb-1">{game.away_team?.abbreviation}</span>
                        <input
                          type="number"
                          placeholder="0"
                          value={gameResults[game.id]?.away_score ?? ''}
                          onChange={(e) => setGameResults(prev => ({
                            ...prev,
                            [game.id]: { ...prev[game.id], away_score: e.target.value }
                          }))}
                          className="w-16 px-3 py-2 border border-slate-300 rounded text-center font-bold text-lg"
                        />
                      </div>
                      <span className="text-slate-400 font-bold text-xl">-</span>
                      <div className="text-center">
                        <span className="text-xs text-slate-500 block mb-1">{game.home_team?.abbreviation}</span>
                        <input
                          type="number"
                          placeholder="0"
                          value={gameResults[game.id]?.home_score ?? ''}
                          onChange={(e) => setGameResults(prev => ({
                            ...prev,
                            [game.id]: { ...prev[game.id], home_score: e.target.value }
                          }))}
                          className="w-16 px-3 py-2 border border-slate-300 rounded text-center font-bold text-lg"
                        />
                      </div>
                    </div>
                    
                    {/* Winner Selection & Save */}
                    <div className="flex items-center gap-2">
                      <select
                        value={gameResults[game.id]?.winner_team_id || ''}
                        onChange={(e) => setGameResults(prev => ({
                          ...prev,
                          [game.id]: { ...prev[game.id], winner_team_id: e.target.value }
                        }))}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded bg-white"
                        data-testid={`winner-select-${game.id}`}
                      >
                        <option value="">Select Winner...</option>
                        <option value={game.away_team.id}>{game.away_team.name} (Away)</option>
                        <option value={game.home_team.id}>{game.home_team.name} (Home)</option>
                      </select>
                      
                      <Button
                        onClick={() => saveGameResult(game.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                        data-testid={`save-result-${game.id}`}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {game.status === 'final' ? 'Update Result' : 'Save Result'}
                      </Button>
                      
                      {game.status === 'final' && (
                        <Button
                          onClick={() => rescoreGame(game.id)}
                          disabled={rescoringGame === game.id}
                          variant="outline"
                          className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                          data-testid={`rescore-game-${game.id}`}
                        >
                          <BarChart2 className="h-4 w-4 mr-1" />
                          {rescoringGame === game.id ? 'Rescoring...' : 'Rescore'}
                        </Button>
                      )}
                    </div>
                    
                    {/* Status indicators */}
                    {game.status === 'final' && (
                      <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-2 rounded">
                        <Check className="h-4 w-4" />
                        Result saved - {game.winner_team_id === game.away_team.id ? game.away_team.name : game.home_team.name} won
                        {game.away_score && game.home_score && (
                          <span className="text-slate-500">({game.away_score} - {game.home_score})</span>
                        )}
                        <span className="ml-auto text-xs text-slate-400">You can update the result or rescore picks</span>
                      </div>
                    )}
                  </div>
                </details>
                
                <LinkedArticleSelect game={game} articles={articles} password={password} onSaved={fetchGames} />

                {/* Game Label */}
                {game.game_label && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                    <span className="text-blue-600 text-sm font-bold">{game.game_label}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPickemPage;
